import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info';

interface ToastProps {
  message: string;
  type?: ToastType;
  duration?: number;
  onClose?: () => void;
  visible: boolean;
}

export function Toast({ 
  message, 
  type = 'info', 
  duration = 3000, 
  onClose,
  visible
}: ToastProps) {
  const [isVisible, setIsVisible] = useState(visible);
  
  // Handle auto-hiding after duration
  useEffect(() => {
    setIsVisible(visible);
    
    if (visible && duration !== Infinity) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        if (onClose) onClose();
      }, duration);
      
      return () => clearTimeout(timer);
    }
  }, [visible, duration, onClose]);
  
  // Handle animation end - notify parent when fully closed
  const handleAnimationEnd = () => {
    if (!isVisible && onClose) {
      onClose();
    }
  };
  
  if (!visible && !isVisible) return null;
  
  return (
    <div 
      className={`
        fixed bottom-4 left-1/2 -translate-x-1/2 z-[110000]
        flex items-center p-4 rounded-xl shadow-sm gap-3 min-w-[300px] max-w-md
        backdrop-blur-md border
        transition-all duration-300 ease-in-out
        ${isVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-10 pointer-events-none'}
        bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 backdrop-blur-md border-border-light dark:border-border-dark
      `}
      onAnimationEnd={handleAnimationEnd}
    >
      {/* Icon */}
      {type === 'success' ? (
        <CheckCircle size={20} className="text-green-400 dark:text-green-300 flex-shrink-0" />
      ) : type === 'error' ? (
        <AlertCircle size={20} className="text-red-400 dark:text-red-300 flex-shrink-0" />
      ) : (
        <AlertCircle size={20} className="text-blue-400 dark:text-blue-300 flex-shrink-0" />
      )}
      
      {/* Message */}
      <div className="flex-1 text-sm font-semibold text-text-primary-light dark:text-text-primary-dark font-heading">
        {message}
      </div>
      
      {/* Close button */}
      <button 
        onClick={() => {
          setIsVisible(false);
          if (onClose) onClose();
        }}
        className="p-1 rounded-full hover:bg-text-primary-light/20 dark:hover:bg-text-primary-dark/20 transition-colors flex-shrink-0"
        aria-label="Close notification"
      >
        <X size={16} className="text-text-primary-light dark:text-text-primary-dark" />
      </button>
    </div>
  );
}

// Toast container and manager
interface ToastManagerProps {
  position?: 'top-center' | 'bottom-center';
}

interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
  duration: number;
}

// Create a toast context to manage toasts across the application
export const ToastContext = React.createContext<{
  showToast: (message: string, type?: ToastType, duration?: number) => void;
}>({
  showToast: () => {},
});

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  
  const showToast = (message: string, type: ToastType = 'info', duration: number = 3000) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, message, type, duration }]);
  };
  
  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };
  
  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      
      {/* Toast container */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[110000] flex flex-col gap-2 items-center">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onClose={() => removeToast(toast.id)}
            visible={true}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// Hook to use the toast system
export function useToast() {
  const context = React.useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
} 